import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, doc, setDoc } from 'firebase/firestore';
import { db } from '../../../../firebase/config';
import emailChanges from '../../../../data/emailChanges';
import { convertObjectDates } from '../../../../utils/firebase-timestamp-transformation';
import './AdditionalDetailsTab.scss';

const AdditionalDetailsTab = () => {
  const [additionalDetails, setAdditionalDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [migrating, setMigrating] = useState(null);

  useEffect(() => {
    fetchAdditionalDetails();
  }, []);

  const fetchAdditionalDetails = async () => {
    try {
      setLoading(true);
      const additionalDetailsRef = collection(db, 'additionalDetails');
      const querySnapshot = await getDocs(additionalDetailsRef);
      
      const detailsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setAdditionalDetails(detailsList);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching additional details:', error);
      setLoading(false);
    }
  };

  // Function to find new email for an employee from emailChanges.js
  const findNewEmail = (oldEmail) => {
    const change = emailChanges.find(change => change.oldEmail === oldEmail);
    return change ? change.newEmail : null;
  };

  // Handle migrate button click
  const handleMigrate = async (details) => {
    const oldEmail = details.id; // Assuming id is the email
    const newEmail = findNewEmail(oldEmail);
    
    if (!newEmail) {
      alert(`No migration mapping found for ${oldEmail} in emailChanges.js`);
      return;
    }
    
    if (!window.confirm(`Are you sure you want to migrate additional details for ${oldEmail} to ${newEmail}?`)) {
      return;
    }
    
    try {
      setMigrating(oldEmail);
      
      // Check if details with the new email already exists
      const additionalDetailsRef = collection(db, 'additionalDetails');
      const newEmailQuery = query(additionalDetailsRef, where('id', '==', newEmail));
      const newEmailSnapshot = await getDocs(newEmailQuery);
      
      if (!newEmailSnapshot.empty) {
        alert(`Additional details for ${newEmail} already exist in the database.`);
        setMigrating(null);
        return;
      }
      
      // Convert any timestamp fields to proper JavaScript Date objects
      const processedDetails = convertObjectDates(details, ['lastUpdated']);
      
      // Create new document with updated email
      const newDetailsData = {
        ...processedDetails,
        id: newEmail,
        oldEmail: oldEmail,
        migrated: true,
        migratedAt: new Date()
      };
      
      // Use the new email as the document ID
      const newDetailsRef = doc(db, 'additionalDetails', newEmail);
      await setDoc(newDetailsRef, newDetailsData);
      
      alert(`Successfully migrated additional details from ${oldEmail} to ${newEmail}`);
      
      // Refresh the list
      fetchAdditionalDetails();
      
    } catch (error) {
      console.error('Error migrating additional details:', error);
      alert(`Error migrating additional details: ${error.message}`);
    } finally {
      setMigrating(null);
    }
  };

  const filteredDetails = searchTerm 
    ? additionalDetails.filter(detail => 
        detail.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        detail.declarationHighestQualification?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : additionalDetails;

  // Format date for display
  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    // If it's a timestamp (has seconds property)
    if (date.seconds) {
      return new Date(date.seconds * 1000).toLocaleDateString();
    }
    
    // If it's already a Date object
    if (date instanceof Date) {
      return date.toLocaleDateString();
    }
    
    // Try to parse as date string
    return new Date(date).toLocaleDateString();
  };

  return (
    <div className="additional-details-tab">
      <div className="details-header">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search by email or qualification..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="refresh-container">
          <button 
            className="refresh-button"
            onClick={fetchAdditionalDetails}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>
      
      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading additional details...</p>
        </div>
      ) : filteredDetails.length > 0 ? (
        <div className="details-table-container">
          <table className="details-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Highest Qualification</th>
                <th>Disability Status</th>
                <th>Conviction Status</th>
                <th>Last Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDetails.map(detail => (
                <tr key={detail.id} className={detail.migrated ? 'migrated-row' : ''}>
                  <td>
                    {detail.id}
                    {detail.migrated && <span className="migrated-badge">Migrated</span>}
                  </td>
                  <td>{detail.declarationHighestQualification || 'N/A'}</td>
                  <td>{detail.declarationDisabilityStatus || 'N/A'}</td>
                  <td>{detail.declarationConvictionStatus || 'N/A'}</td>
                  <td>{formatDate(detail.lastUpdated)}</td>
                  <td>
                    {!detail.migrated && findNewEmail(detail.id) && (
                      <button
                        className="migrate-button"
                        onClick={() => handleMigrate(detail)}
                        disabled={migrating === detail.id}
                      >
                        {migrating === detail.id ? 'Migrating...' : 'Migrate'}
                      </button>
                    )}
                    {detail.migrated && (
                      <span className="already-migrated">Already Migrated</span>
                    )}
                    {!findNewEmail(detail.id) && !detail.migrated && (
                      <span className="no-mapping">No Mapping</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="no-data">
          <p>No additional details found.</p>
        </div>
      )}
    </div>
  );
};

export default AdditionalDetailsTab; 